import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SafetyDashboardRow } from '../../services/reportsApi';
import {
  NEW_ACTIVITY_DECISION_LABELS,
  NEW_ACTIVITY_FROZEN_SCAN_COPY,
  formatNewActivityCountSummary,
  resolveFrozenSignedScanId,
  resolveNewActivityCount,
  submitNewActivityDecision,
  type NewActivityDecisionKind,
} from '../../domain/safetyCases/workspaceNewActivityHandling';
import { formatAfterSignedScanTimestamp } from '../../domain/incidents/afterSignedScanReview';

export type NewActivityDecisionPanelProps = {
  row: SafetyDashboardRow;
  canDecide: boolean;
  busy?: boolean;
  error?: string | null;
  actorUser?: { username?: string | null; email?: string | null } | null;
  onReviewEvidence: () => void;
  onSubmit: (kind: NewActivityDecisionKind, reviewerNote: string) => void;
};

export function useNewActivityDrawerDecision(args: {
  row: SafetyDashboardRow | null;
  setRow: (row: SafetyDashboardRow | null) => void;
  actorUser?: { username?: string | null; email?: string | null } | null;
  canDecide: boolean;
  orgId?: string | null;
  siteId?: string | null;
  reportFrom?: string | null;
  reportTo?: string | null;
  liveCasePathForRow: (row: SafetyDashboardRow) => string | null;
  onRefresh: () => Promise<void>;
  onNavigate: (path: string) => void;
  onReviewEvidenceFallback: (row: SafetyDashboardRow) => void;
  onGlobalError?: (message: string | null) => void;
}) {
  const {
    row,
    setRow,
    actorUser,
    canDecide,
    orgId,
    siteId,
    reportFrom,
    reportTo,
    liveCasePathForRow,
    onRefresh,
    onNavigate,
    onReviewEvidenceFallback,
    onGlobalError,
  } = args;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setBusy(false);
    setError(null);
  }, []);

  const close = useCallback(() => {
    reset();
    setRow(null);
  }, [reset, setRow]);

  const open = useCallback((next: SafetyDashboardRow) => {
    reset();
    setRow(next);
  }, [reset, setRow]);

  const reviewEvidence = useCallback(() => {
    const current = row;
    close();
    if (current) onReviewEvidenceFallback(current);
  }, [close, onReviewEvidenceFallback, row]);

  const submit = useCallback(async (kind: NewActivityDecisionKind, reviewerNote: string) => {
    if (!row) return;
    setBusy(true);
    setError(null);
    onGlobalError?.(null);
    const result = await submitNewActivityDecision({
      row,
      kind,
      actor: String(actorUser?.username || actorUser?.email || '').trim() || 'reviewer',
      reviewerNote,
      orgId,
      siteId,
      reportFrom,
      reportTo,
      liveCasePath: liveCasePathForRow(row),
    });
    if (!result.ok) {
      setError(result.message);
      setBusy(false);
      return;
    }
    close();
    setBusy(false);
    await onRefresh();
    if (result.navigateTo) onNavigate(result.navigateTo);
  }, [
    actorUser?.email,
    actorUser?.username,
    close,
    liveCasePathForRow,
    onGlobalError,
    onNavigate,
    onRefresh,
    orgId,
    reportFrom,
    reportTo,
    row,
    siteId,
  ]);

  return {
    open,
    busy,
    error,
    drawerProps: {
      open: Boolean(row),
      row,
      onClose: close,
      onReviewEvidence: reviewEvidence,
      canDecide,
      decisionBusy: busy,
      decisionError: error,
      actorUser,
      onDecision: (kind: NewActivityDecisionKind, reviewerNote: string) => {
        void submit(kind, reviewerNote);
      },
    },
  };
}

export function NewActivityDecisionPanel({
  row,
  canDecide,
  busy = false,
  error = null,
  actorUser,
  onReviewEvidence,
  onSubmit,
}: NewActivityDecisionPanelProps) {
  const [note, setNote] = useState('');
  const [confirmKind, setConfirmKind] = useState<NewActivityDecisionKind | null>(null);
  const activityCount = resolveNewActivityCount(row);
  const frozenScanId = resolveFrozenSignedScanId(row);
  const signedAt = String(row.report_finalized_at || row.latest_scan_signed_at || '').trim();
  const latestObservation = String(
    row.source_workflow_updated_at || row.latest_observation_at || row.updated_at || '',
  ).trim();
  const actor = String(actorUser?.username || actorUser?.email || '').trim() || 'reviewer';

  const request = (kind: NewActivityDecisionKind) => {
    if (kind === 'acknowledge_continue_monitoring' || kind === 'reopen_case') {
      setConfirmKind(kind);
      return;
    }
    onSubmit(kind, note);
  };

  const confirm = () => {
    if (!confirmKind) return;
    onSubmit(confirmKind, note);
    setConfirmKind(null);
  };

  return (
    <Stack spacing={1.5} data-testid="new-activity-decision-panel">
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          New activity decision
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {NEW_ACTIVITY_FROZEN_SCAN_COPY}
        </Typography>
      </Box>

      <Box data-testid="new-activity-decision-summary">
        <Typography variant="body2">
          {`Signed Scan: ${signedAt ? formatAfterSignedScanTimestamp(signedAt) : 'Not recorded'}`}
        </Typography>
        {frozenScanId ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {`Frozen record id retained: ${frozenScanId}`}
          </Typography>
        ) : null}
        <Typography variant="body2">
          {formatNewActivityCountSummary({
            count: activityCount,
            signedScanBoundary: signedAt || null,
          }) || 'Observation count for the current new-activity window is not recorded on this summary'}
        </Typography>
        <Typography variant="body2">
          {`Latest observation: ${latestObservation ? formatAfterSignedScanTimestamp(latestObservation) : 'Not recorded'}`}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {`Acting as ${actor}`}
        </Typography>
      </Box>

      {canDecide ? (
        <TextField
          label="Reviewer note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          multiline
          minRows={2}
          fullWidth
          size="small"
          disabled={busy}
          helperText="Required for acknowledge and reopen. Optional for Scan actions."
          data-testid="new-activity-reviewer-note"
        />
      ) : (
        <Alert severity="info">
          Read-only access. You can review evidence, but new-activity decisions require Reviewer permissions.
        </Alert>
      )}

      {error ? <Alert severity="error">{error}</Alert> : null}

      {confirmKind ? (
        <Alert
          severity="warning"
          data-testid="new-activity-confirm"
          action={(
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={() => setConfirmKind(null)} disabled={busy}>
                Cancel
              </Button>
              <Button color="inherit" size="small" variant="outlined" onClick={confirm} disabled={busy}>
                Confirm
              </Button>
            </Stack>
          )}
        >
          {`Confirm ${NEW_ACTIVITY_DECISION_LABELS[confirmKind].toLowerCase()}? The signed Scan stays frozen.`}
        </Alert>
      ) : null}

      {canDecide ? (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {(Object.keys(NEW_ACTIVITY_DECISION_LABELS) as NewActivityDecisionKind[]).map((kind) => (
            <Button
              key={kind}
              size="small"
              variant={kind === 'acknowledge_continue_monitoring' ? 'contained' : 'outlined'}
              disabled={busy}
              onClick={() => request(kind)}
              data-testid={`new-activity-action-${kind}`}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {NEW_ACTIVITY_DECISION_LABELS[kind]}
            </Button>
          ))}
        </Stack>
      ) : null}

      <Button
        size="small"
        variant="text"
        onClick={onReviewEvidence}
        disabled={busy}
        sx={{ alignSelf: 'flex-start', px: 0, textTransform: 'none' }}
        data-testid="new-activity-review-evidence"
      >
        Review evidence (full /incidents fallback)
      </Button>
    </Stack>
  );
}

export default NewActivityDecisionPanel;
